import { useState } from "react";
import {
  AlertTriangle,
  Check,
  Copy,
  Download,
  KeyRound,
  Loader2,
  ShieldCheck,
  ShieldOff,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  useBeginMfaSetup,
  useDisableMfa,
  useEnableMfa,
  useMfaStatus,
  useRegenerateRecoveryCodes,
  type MfaSetup,
} from "../hooks/useMfa";

const BORDER = "hsl(210,30%,85%)";

type Mode = "idle" | "setup" | "codes" | "regenerate" | "disable";

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function ErrorBox({ message }: { message: string }) {
  return (
    <div
      className="flex items-center gap-2 rounded-md border px-3 py-2 text-sm"
      style={{
        borderColor: "hsl(0,70%,80%)",
        background: "hsl(0,70%,96%)",
        color: "hsl(0,70%,35%)",
      }}
    >
      <AlertTriangle className="h-4 w-4 flex-shrink-0" strokeWidth={1.8} />
      {message}
    </div>
  );
}

function errorMessage(err: unknown, fallback: string): string {
  return err instanceof Error ? err.message : fallback;
}

/** Accepts a 6-digit authenticator code or a recovery code. */
function SecondFactorInput(props: {
  id: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div className="space-y-2">
      <Label htmlFor={props.id}>Authenticator or recovery code</Label>
      <Input
        id={props.id}
        value={props.value}
        onChange={(e) => props.onChange(e.target.value.toUpperCase())}
        autoComplete="one-time-code"
        placeholder="123456 or XXXX-XXXX"
        maxLength={9}
      />
    </div>
  );
}

export function TwoFactorCard() {
  const { data: status, isLoading } = useMfaStatus();
  const beginSetup = useBeginMfaSetup();
  const enable = useEnableMfa();
  const disable = useDisableMfa();
  const regenerate = useRegenerateRecoveryCodes();

  const [mode, setMode] = useState<Mode>("idle");
  const [setup, setSetup] = useState<MfaSetup | null>(null);
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState<"secret" | "codes" | null>(null);

  function reset() {
    setMode("idle");
    setSetup(null);
    setCode("");
    setPassword("");
    setRecoveryCodes([]);
    setError(null);
  }

  async function copy(text: string, what: "secret" | "codes") {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(what);
      setTimeout(() => setCopied(null), 2000);
    } catch {
      // Clipboard can be blocked (http, permissions) — the text is on screen.
    }
  }

  function downloadCodes() {
    const body = [
      "PhazeX Hub — two-factor recovery codes",
      `Generated: ${new Date().toLocaleString("en-GB")}`,
      "",
      "Each code can be used once to sign in if you lose your authenticator.",
      "",
      ...recoveryCodes,
      "",
    ].join("\n");
    const url = URL.createObjectURL(new Blob([body], { type: "text/plain" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = "phazex-hub-recovery-codes.txt";
    a.click();
    URL.revokeObjectURL(url);
  }

  async function startSetup() {
    setError(null);
    try {
      setSetup(await beginSetup.mutateAsync());
      setCode("");
      setMode("setup");
    } catch (err) {
      setError(errorMessage(err, "Failed to start setup"));
    }
  }

  async function confirmSetup() {
    if (!/^\d{6}$/.test(code)) {
      setError("Enter the 6-digit code shown in your authenticator app");
      return;
    }
    setError(null);
    try {
      const res = await enable.mutateAsync(code);
      setRecoveryCodes(res.recoveryCodes);
      setSetup(null);
      setCode("");
      setMode("codes");
    } catch (err) {
      setError(errorMessage(err, "Failed to enable two-factor authentication"));
    }
  }

  async function submitRegenerate() {
    if (!code.trim()) {
      setError("Enter a code to confirm");
      return;
    }
    setError(null);
    try {
      const res = await regenerate.mutateAsync(code.trim());
      setRecoveryCodes(res.recoveryCodes);
      setCode("");
      setMode("codes");
    } catch (err) {
      setError(errorMessage(err, "Failed to regenerate recovery codes"));
    }
  }

  async function submitDisable() {
    if (!password || !code.trim()) {
      setError("Enter your password and a code to confirm");
      return;
    }
    setError(null);
    try {
      await disable.mutateAsync({ password, code: code.trim() });
      reset();
    } catch (err) {
      setError(errorMessage(err, "Failed to disable two-factor authentication"));
    }
  }

  const header = (
    <div className="flex items-center justify-between gap-2">
      <div className="flex items-center gap-2">
        <ShieldCheck className="h-4 w-4" strokeWidth={1.5} />
        <h3 className="text-sm font-semibold text-foreground">
          Two-Factor Authentication
        </h3>
      </div>
      {status && (
        <span
          className="rounded-full px-2 py-0.5 text-[11px] font-medium"
          style={
            status.enabled
              ? { background: "hsl(142,55%,94%)", color: "hsl(142,72%,28%)" }
              : { background: "hsl(210,16%,94%)", color: "hsl(210,10%,40%)" }
          }
        >
          {status.enabled ? "Enabled" : "Off"}
        </span>
      )}
    </div>
  );

  if (isLoading || !status) {
    return (
      <Card className="md:col-span-2">
        <CardContent className="space-y-4 pt-6">
          {header}
          <p className="text-sm text-muted-foreground">Loading…</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="md:col-span-2">
      <CardContent className="space-y-4 pt-6">
        {header}

        {mode === "codes" ? (
          <div className="space-y-3">
            <div
              className="flex items-start gap-2 rounded-md border px-3 py-2 text-[12px]"
              style={{
                borderColor: "hsl(38,92%,75%)",
                background: "hsl(38,92%,96%)",
                color: "hsl(38,92%,25%)",
              }}
            >
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" strokeWidth={1.8} />
              <span>
                Save these recovery codes somewhere safe (a password manager is
                ideal). Each one works once if you lose your phone.{" "}
                <strong>They won't be shown again.</strong>
              </span>
            </div>
            <div
              className="grid grid-cols-2 gap-x-6 gap-y-1.5 rounded-md border px-4 py-3 font-mono text-sm sm:grid-cols-5"
              style={{ borderColor: BORDER, background: "hsl(210,16%,97%)" }}
            >
              {recoveryCodes.map((c) => (
                <span key={c}>{c}</span>
              ))}
            </div>
            <div className="flex flex-wrap items-center justify-end gap-2">
              <Button variant="outline" onClick={() => copy(recoveryCodes.join("\n"), "codes")}>
                {copied === "codes" ? (
                  <Check className="mr-1 h-4 w-4" strokeWidth={1.8} />
                ) : (
                  <Copy className="mr-1 h-4 w-4" strokeWidth={1.5} />
                )}
                {copied === "codes" ? "Copied" : "Copy"}
              </Button>
              <Button variant="outline" onClick={downloadCodes}>
                <Download className="mr-1 h-4 w-4" strokeWidth={1.5} />
                Download
              </Button>
              <Button onClick={reset}>
                <Check className="mr-1 h-4 w-4" strokeWidth={1.8} />
                I've saved these codes
              </Button>
            </div>
          </div>
        ) : mode === "setup" && setup ? (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              confirmSetup();
            }}
            className="space-y-4"
          >
            <ol className="list-decimal space-y-1 pl-5 text-sm text-muted-foreground">
              <li>
                Install an authenticator app — Google Authenticator, Microsoft
                Authenticator, Authy, 1Password or Bitwarden.
              </li>
              <li>Scan this QR code with the app (or enter the key manually).</li>
              <li>Enter the 6-digit code the app shows to finish.</li>
            </ol>
            <div className="flex flex-wrap items-start gap-6">
              <img
                src={setup.qrDataUrl}
                alt="QR code for your authenticator app"
                width={180}
                height={180}
                className="rounded-md border bg-white p-1"
                style={{ borderColor: BORDER }}
              />
              <div className="min-w-0 flex-1 space-y-4">
                <div className="space-y-1">
                  <Label>Setup key</Label>
                  <div className="flex items-center gap-2">
                    <code
                      className="break-all rounded-md border px-3 py-2 font-mono text-[13px]"
                      style={{ borderColor: BORDER, background: "hsl(210,16%,97%)" }}
                    >
                      {setup.secret.match(/.{1,4}/g)?.join(" ")}
                    </code>
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      aria-label="Copy setup key"
                      onClick={() => copy(setup.secret, "secret")}
                    >
                      {copied === "secret" ? (
                        <Check className="h-4 w-4" strokeWidth={1.8} />
                      ) : (
                        <Copy className="h-4 w-4" strokeWidth={1.5} />
                      )}
                    </Button>
                  </div>
                  <p className="text-[11px] text-muted-foreground">
                    Time-based, 6 digits, 30 seconds.
                  </p>
                </div>
                <div className="max-w-[220px] space-y-2">
                  <Label htmlFor="mfa-setup-code">Code from app</Label>
                  <Input
                    id="mfa-setup-code"
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    placeholder="123456"
                    maxLength={6}
                    autoFocus
                    className="tracking-[0.3em]"
                  />
                </div>
              </div>
            </div>
            {error && <ErrorBox message={error} />}
            <div className="flex items-center justify-end gap-2">
              <Button type="button" variant="outline" onClick={reset} disabled={enable.isPending}>
                Cancel
              </Button>
              <Button type="submit" disabled={enable.isPending}>
                {enable.isPending ? (
                  <Loader2 className="mr-1 h-4 w-4 animate-spin" strokeWidth={1.5} />
                ) : (
                  <Check className="mr-1 h-4 w-4" strokeWidth={1.8} />
                )}
                Verify &amp; Enable
              </Button>
            </div>
          </form>
        ) : mode === "regenerate" ? (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              submitRegenerate();
            }}
            className="max-w-md space-y-3"
          >
            <p className="text-sm text-muted-foreground">
              This replaces all your existing recovery codes.
            </p>
            <SecondFactorInput id="mfa-regen-code" value={code} onChange={setCode} />
            {error && <ErrorBox message={error} />}
            <div className="flex items-center justify-end gap-2">
              <Button type="button" variant="outline" onClick={reset} disabled={regenerate.isPending}>
                Cancel
              </Button>
              <Button type="submit" disabled={regenerate.isPending}>
                <KeyRound className="mr-1 h-4 w-4" strokeWidth={1.5} />
                Generate New Codes
              </Button>
            </div>
          </form>
        ) : mode === "disable" ? (
          <form
            autoComplete="off"
            onSubmit={(e) => {
              e.preventDefault();
              submitDisable();
            }}
            className="max-w-md space-y-3"
          >
            <p className="text-sm text-muted-foreground">
              Your account will be protected by your password only.
            </p>
            <div className="space-y-2">
              <Label htmlFor="mfa-disable-pw">Current Password</Label>
              <Input
                id="mfa-disable-pw"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
              />
            </div>
            <SecondFactorInput id="mfa-disable-code" value={code} onChange={setCode} />
            {error && <ErrorBox message={error} />}
            <div className="flex items-center justify-end gap-2">
              <Button type="button" variant="outline" onClick={reset} disabled={disable.isPending}>
                Cancel
              </Button>
              <Button type="submit" variant="destructive" disabled={disable.isPending}>
                <ShieldOff className="mr-1 h-4 w-4" strokeWidth={1.5} />
                Disable
              </Button>
            </div>
          </form>
        ) : status.enabled ? (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Signing in requires your password and a code from your
              authenticator app. Enabled on {formatDate(status.enabledAt)}.
            </p>
            <p
              className="text-sm"
              style={
                status.recoveryCodesRemaining <= 3
                  ? { color: "hsl(38,92%,30%)" }
                  : undefined
              }
            >
              {status.recoveryCodesRemaining} of 10 recovery codes remaining
              {status.recoveryCodesRemaining <= 3 && " — consider generating new ones."}
            </p>
            {error && <ErrorBox message={error} />}
            <div className="flex flex-wrap items-center gap-2">
              <Button variant="outline" onClick={() => setMode("regenerate")}>
                <KeyRound className="mr-1 h-4 w-4" strokeWidth={1.5} />
                New Recovery Codes
              </Button>
              <Button variant="outline" onClick={() => setMode("disable")}>
                <ShieldOff className="mr-1 h-4 w-4" strokeWidth={1.5} />
                Disable
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Add a second step to sign-in using an authenticator app on your
              phone. Even if someone learns your password, they can't sign in
              without your phone.
            </p>
            {error && <ErrorBox message={error} />}
            <Button onClick={startSetup} disabled={beginSetup.isPending}>
              {beginSetup.isPending ? (
                <Loader2 className="mr-1 h-4 w-4 animate-spin" strokeWidth={1.5} />
              ) : (
                <ShieldCheck className="mr-1 h-4 w-4" strokeWidth={1.5} />
              )}
              Enable Two-Factor Authentication
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
